import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';

import Button from '@/components/Button';
import ErrorAccordion from '@/components/ErrorAccordion';
import Icon from '@/components/Icon';
import Text from '@/components/Text';
import {
  ArchiveFileTypes,
  CadFileTypes,
  PdfFileTypes,
} from '@/config/filesTypes';
import { cncHandoffRedirectUrl, postCncHandoff } from '@/services/cncHandoff';

import FileInput, { getExtensionsString } from '../Form/Fields/FileInput';

const cncUploadFileTypes = {
  ...PdfFileTypes,
  ...CadFileTypes,
  ...ArchiveFileTypes,
};

const trustBadges = [
  { emoji: '🔒', label: '100% Confidential & Secure' },
  { emoji: '⚡', label: 'Instant 30-Second Analysis' },
  { emoji: '💳', label: 'No Credit Card Required' },
];

const getHandoffErrorMessage = (error: unknown): string => {
  if (!(error instanceof AxiosError)) {
    return 'Something went wrong uploading your file.';
  }

  if (!error.response) {
    return 'Could not reach the quoting service. Check your connection and try again.';
  }

  const detail = error.response.data?.detail;
  if (typeof detail === 'string' && detail.length > 0) {
    return detail;
  }

  return `Upload failed (HTTP ${error.response.status}).`;
};

const CncRfqUpload = () => {
  const [file, setFile] = useState<File | undefined>();

  const {
    mutate: submitHandoff,
    isLoading: isUploading,
    error: handoffError,
  } = useMutation({
    mutationFn: postCncHandoff,
    mutationKey: ['postCncHandoff'],
    onSuccess: (handoff) => {
      window.location.href = cncHandoffRedirectUrl(handoff);
    },
  });

  const error = handoffError ? getHandoffErrorMessage(handoffError) : undefined;

  const onSubmit = () => {
    if (file) submitHandoff(file);
  };

  return (
    <div className="flex w-full flex-col gap-6 rounded-xl border border-jb-neutral-100/[29%] bg-buttonCNC/10 p-4 backdrop-blur-sm md:p-6">
      <Text variant="h5" className="font-semibold text-neutral-0">
        Instant Estimate
      </Text>

      <div className="relative overflow-hidden rounded-2xl bg-uploadCNC/30">
        <svg
          aria-hidden
          className="size-full pointer-events-none absolute inset-0 h-full w-full"
          fill="none"
        >
          <rect
            x="1"
            y="1"
            width="calc(100% - 2px)"
            height="calc(100% - 2px)"
            rx="15"
            ry="15"
            stroke="#5B34B4"
            strokeWidth="1"
            strokeDasharray="7 8"
            strokeLinecap="round"
          />
        </svg>

        <div
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-0 -z-10 h-full w-1/2 -translate-x-1/2 bg-gradient-to-r via-buttonCNC"
          style={{
            backgroundImage:
              'linear-gradient(to bottom, rgba(91, 52, 180, 0.35) 1px, transparent 1px), linear-gradient(to right, rgba(91, 52, 180, 0.35) 1px, transparent 1px)',
            backgroundSize: '23px 23px',
            backgroundPosition: 'top center',
            backgroundRepeat: 'repeat',
          }}
        />
        <FileInput
          variant="unstyled"
          containerClassName="p-0"
          alwaysShowInput
          showFileList={false}
          canHaveErrorMessage={false}
          value={file}
          onChange={(value) =>
            setFile(value instanceof File ? value : undefined)
          }
          error={error}
          accept={cncUploadFileTypes}
          isLoading={isUploading}
          maxSize={100 * 1024 * 1024}
          clearable
        >
          <div className="flex flex-col items-center gap-3.5 px-6 py-8 text-center">
            <div className="size-14 flex items-center justify-center rounded-xl bg-jb-primary-500 p-4">
              <Icon
                icon="material-symbols:cloud-upload-outline"
                className="text-[28px] text-neutral-0"
              />
            </div>
            <Text className="max-w-sm text-center text-neutral-0">
              Upload your CAD or technical drawing here to generate an instant
              estimate sample.
            </Text>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {getExtensionsString(cncUploadFileTypes)
                .split(', ')
                .map((ext: string) => (
                  <span
                    key={ext}
                    className="text-body-caption rounded-[7px] border border-jb-primary-600 bg-jb-primary-600/[15%] px-[11px] py-[6px] text-jb-primary-300"
                  >
                    {ext}
                  </span>
                ))}
            </div>
            {file && (
              <div className="relative flex flex-col gap-3 px-4 pb-4 md:px-6 md:pb-6">
                <div className="flex items-center gap-2 rounded-lg border border-jb-success-400/30 bg-jb-success-400/10 px-3 py-2.5">
                  <Icon
                    icon="material-symbols:check-circle-outline"
                    className="text-lg text-jb-success-400"
                  />
                  <Text variant="body-small" className="text-jb-success-400">
                    You have successfully uploaded the file.
                  </Text>
                </div>
                <div className="flex items-center justify-between gap-3 rounded-lg border border-jb-neutral-100/[15%] bg-neutral-0/5 px-3 py-2.5">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <Icon
                      icon="material-symbols:description-outline"
                      className="shrink-0 text-lg text-jb-primary-300"
                    />
                    <div className="flex min-w-0 flex-col">
                      <Text
                        variant="body-small"
                        className="truncate text-neutral-0"
                      >
                        {file.name}
                      </Text>
                      <Text
                        className="text-jb-neutral-600"
                        variant="body-caption"
                      >
                        {Math.round(file.size / 1024)} KB
                      </Text>
                    </div>
                  </div>
                  <button
                    type="button"
                    aria-label="Remove file"
                    onClick={() => setFile(undefined)}
                    className="shrink-0 text-jb-neutral-600 transition-colors hover:text-neutral-0"
                  >
                    <Icon
                      icon="material-symbols:delete-outline"
                      className="text-lg"
                    />
                  </button>
                </div>
              </div>
            )}

            {error && (
              <div className="px-4 pb-4 md:px-6 md:pb-6">
                <div className="flex items-start gap-2 rounded-lg border border-jb-alert-400/30 bg-jb-alert-400/10 px-3 py-2.5">
                  <Icon
                    icon="material-symbols:error-outline"
                    className="mt-0.5 shrink-0 text-lg text-jb-alert-400"
                  />
                  <ErrorAccordion
                    error={error}
                    errorTextClassName="!text-jb-alert-400"
                  />
                </div>
              </div>
            )}
          </div>
        </FileInput>

        <AnimatePresence>
          {isUploading && (
            <motion.div
              initial={{ top: '0%' }}
              animate={{ top: '100%' }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.6, repeat: Infinity, ease: 'linear' }}
              className="pointer-events-none absolute inset-x-0 h-0.5 bg-gradient-to-r from-transparent via-jb-primary-400 to-transparent"
            />
          )}
        </AnimatePresence>
      </div>

      {file && (
        <Button
          variant="jbPrimary"
          className="w-fit self-center text-white"
          isLoading={isUploading}
          onClick={onSubmit}
        >
          Get instant quote
        </Button>
      )}

      <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5">
        {trustBadges.map(({ emoji, label }) => (
          <div key={label} className="flex items-center gap-1.5">
            <span aria-hidden className="text-xs">
              {emoji}
            </span>
            <Text className="text-neutral-400" variant="body-caption">
              {label}
            </Text>
          </div>
        ))}
        <div className="flex flex-col items-center gap-1 text-[12px]">
          <Text className="text-center text-neutral-400" variant="body-caption">
            Files are encrypted in transit, stored securely for quoting and
            support purposes, and never used to train public AI models.
          </Text>
          <Text className="text-center text-neutral-400" variant="body-caption">
            Mutual NDA available upon request.
          </Text>
        </div>
      </div>
    </div>
  );
};

export default CncRfqUpload;
